import * as amplitude from '@amplitude/analytics-browser'

import { Component } from 'react'
import { getOneTrustConsent } from '../utils/onetrust-utils'
import { AMPLITUDE_API_KEY } from '../api/util'

interface QueuedEvent {
  name: string
  data: any
}

export class Tracker extends Component {
  private hasPerformanceConsent: boolean | null = null // null = not determined yet
  private eventQueue: QueuedEvent[] = []
  private removeConsentListener?: () => void
  private initialized = false

  constructor(props: any) {
    super(props)

    // Set up consent tracking
    this.removeConsentListener = getOneTrustConsent((consentState) => {
      const hasPerformanceConsent = consentState.hasPerformanceConsent
      const isFirstCallback = this.hasPerformanceConsent === null
      const hadConsent = this.hasPerformanceConsent === true
      this.hasPerformanceConsent = hasPerformanceConsent

      // On first callback, process or clear queued events
      if (isFirstCallback) {
        if (hasPerformanceConsent) {
          // Initialize Amplitude before processing queue
          this.initializeIfNeeded()
          this.processQueuedEvents()
        } else {
          // Clear the queue without processing if no consent
          this.eventQueue = []
        }
      } else if (hasPerformanceConsent && !hadConsent) {
        // Initialize Amplitude when consent is granted after initial load
        this.initializeIfNeeded()
        // Re-enable tracking if it was previously opted out
        if (this.initialized) {
          amplitude.setOptOut(false)
        }
      } else if (!hasPerformanceConsent && hadConsent) {
        // Handle consent withdrawal - opt out of Amplitude tracking
        this.optOutOfTracking()
      }
    })

    // Public methods
    this.trackRestaurantPageUtmSource =
      this.trackRestaurantPageUtmSource.bind(this)
    this.trackAddItem = this.trackAddItem.bind(this)
    this.trackPlaceOrder = this.trackPlaceOrder.bind(this)
    this.trackPlaceOrderError = this.trackPlaceOrderError.bind(this)
    this.trackAccountLogin = this.trackAccountLogin.bind(this)
    this.trackAccountLogout = this.trackAccountLogout.bind(this)
  }

  componentWillUnmount() {
    this.removeConsentListener?.()
  }

  private processQueuedEvents() {
    // Process all queued events
    const events = [...this.eventQueue]
    this.eventQueue = []

    events.forEach((event) => {
      this.trackInternal(event.name, event.data)
    })
  }
  private initializeIfNeeded() {
    if (!this.initialized) {
      amplitude.init(AMPLITUDE_API_KEY, undefined, { disableCookies: true })
      this.initialized = true
    }
  }

  private optOutOfTracking() {
    if (this.initialized) {
      // Set opt out for Amplitude
      amplitude.setOptOut(true)
      // Clear any queued events
      this.eventQueue = []
    }
  }

  private trackInternal(name: string, data: any) {
    if (!this.hasPerformanceConsent) {
      return
    }

    // Initialize Amplitude if needed
    this.initializeIfNeeded()
    amplitude.track(name, data)
  }

  private track(name: string, data: any) {
    // If consent hasn't been determined yet, queue the event
    if (this.hasPerformanceConsent === null) {
      this.eventQueue.push({ name, data })
      return
    }

    // Track immediately if we have consent
    this.trackInternal(name, data)
  }

  trackRestaurantPageUtmSource(utmSource: string, restaurant_guid: string) {
    this.track('Visit COO Site', {
      utm_source: utmSource,
      restaurant_guid: restaurant_guid
    })
  }

  trackAddItem(restaurant_guid: string, logged_in: boolean) {
    this.track('Add COO Item', {
      restaurant_guid: restaurant_guid,
      logged_in: logged_in
    })
  }

  trackPlaceOrder(restaurant_guid: string, logged_in: boolean) {
    this.track('Place COO Order', {
      restaurant_guid: restaurant_guid,
      logged_in: logged_in
    })
  }

  trackPlaceOrderError(restaurant_guid: string, logged_in: boolean) {
    this.track('Error Placing COO Order', {
      restaurant_guid: restaurant_guid,
      logged_in: logged_in
    })
  }

  trackAccountLogin(restaurant_guid: string) {
    this.track('Guest Logged In', {
      restaurant_guid: restaurant_guid
    })
  }

  trackAccountLogout(restaurant_guid: string) {
    this.track('Guest Logged Out', {
      restaurant_guid: restaurant_guid
    })
  }
}

// Singleton instance used for tracking events on the client
const tracker = new Tracker({})

export const useTracker = () => {
  return tracker
}
